<template>
  <div class="about">
    <page-header :title="$t('team.edit')" :desc="model.name">
      <li class="breadcrumb-item">
        <router-link :to="{name:'schedules.index'}">{{ $tc('schedule.name', 2) }}</router-link>
      </li>
      <li class="breadcrumb-item">
        <router-link :to="{name:'schedules.show', params: { uuid: model.schedule.uuid }}">{{ model.schedule.name }}
        </router-link>
      </li>
      <li class="breadcrumb-item">{{ $tc('team.name', 2) }}</li>
      <li class="breadcrumb-item">{{ model.name }}</li>
    </page-header>
    <page-content>
      <ntm-block :title="$tc('action.name', 2)">
        <template slot="options">
          <button type="button"
                  @click="onSubmit()"
                  class="btn btn-sm btn-success">{{ $t('save') }}
          </button>
          <button type="button"
                  @click="deleteTeam(model.uuid)"
                  class="btn btn-sm btn-danger">{{ $t('team.removeFromSchedule') }}
          </button>
        </template>
      </ntm-block>
      <div class="row">
        <div class="col-xl-6">
          <ntm-block :title="$tc('image.name', 1)" :init-collapse="false">
            <template slot="options">
              <span>{{ $t('ratio') }}: <span class="font-weight-bold">16:10</span>, {{ $t('size') }}: <span class="font-weight-bold">1280x800</span></span>
            </template>
            <ntm-crop :aspect-ratio="16/10" :max-width="1280" :max-height="800" conversion="lg" entity-type="team"
                      :entity-uuid="$route.params.team" collection="profile"></ntm-crop>
          </ntm-block>
          <ntm-block :title="$t('image.jersey')" :init-collapse="false">
            <template slot="options">
              <span>{{ $t('ratio') }}: <span class="font-weight-bold">3:4</span>, {{ $t('size') }}: <span class="font-weight-bold">600x800</span></span>
            </template>
            <ntm-crop :aspect-ratio="3/4" :max-width="600" :max-height="800" conversion="lg" entity-type="team"
                      :entity-uuid="$route.params.team" collection="jersey"></ntm-crop>
          </ntm-block>
          <form @submit.prevent="onSubmit()">
            <ntm-block :title="model.name">
              <formly-form :form="form" :model="model" :fields="fields"></formly-form>
              <div class="btn-group mb-3">
                <button class="btn btn-success">{{ $t('save') }}</button>
              </div>
            </ntm-block>
          </form>
        </div>
        <div class="col-xl-6">
          <ntm-block :collapsable="false" :title="$tc('player.name', 2)">
            <div class="mb-2 d-flex flex-wrap">
              <button @click="addPlayer = true" class="btn btn-info btn-sm mr-2 mb-2">{{ $t('player.add') }}</button>

              <button @click="addFromFile()" class="btn btn-info btn-sm mr-2 mb-2">
                {{ $t('team.rosterImportWithAI') }}
              </button>
              <button @click="showAddPlayersFromClubTeamsModal()" :disabled="!clubTeams.length"
                      class="btn btn-info btn-sm mr-2 mb-2">{{ $t('team.addPlayersFromClubTeams') }}
              </button>
              <add-team-from-application class="mr-2 mb-2" :button-text="$t('schedule.team.import_roster_from_application')" @applicationSelected="applyTeamApplicationToTeam" :close-after-select="true" :types="[]"></add-team-from-application>
            </div>
            <div class="row">
              <div class="col-12">
                <div class="form-group formly-input">
                  <v-select @search="searchPlayer" :filter="(options) => options" @option:selected="selectPlayer"
                            v-model="selectedPlayer" :options="playerFilteredOptions" inputId="playerSearch"
                            label="name"
                            :placeholder="$tc('player.searchAndAdd')">
                    <template #option="option">
                      {{ option.name }}
                    </template>
                    <template #no-options>
                      <p class="info">{{ $tc('player.searchAndAdd') }}</p>
                    </template>
                  </v-select>
                </div>
              </div>
              <div class="col-12">
                <div class="table-responsive">
                  <ntm-table :show-columns="showColumns" :data="selectedPlayersForTable"
                  >
                    <template v-slot:position="{row}">
                      <button @click="openPositionModal(row)" class="btn btn-sm btn-outline-secondary">
                        {{ row.position || '—' }}
                      </button>
                    </template>
                    <template v-slot:tag="{row}">
                      <button :data-testid="'roster-tags-' + row.uuid" @click="openTagModal(row)" class="btn btn-sm btn-outline-secondary">
                        <template v-if="row.player_tags && row.player_tags.length">
                          <span v-for="tag in row.player_tags" :key="tag.uuid" class="badge mr-1" :style="tagBadgeStyle(tag)">{{ tag.name }}</span>
                        </template>
                        <span v-else>—</span>
                      </button>
                    </template>
                    <template v-slot:actions="{row}">
                      <button @click="deletePlayerFromTeam(row.uuid)" class="btn btn-sm btn-danger" v-tooltip="$t('tooltip.deletePlayerFromTeam')">
                        <i class="fa fa-fw fa-trash"></i>
                      </button>
                    </template>
                  </ntm-table>
                </div>
              </div>
            </div>
          </ntm-block>
        </div>
        <div class="col-12">
          <ntm-block title="Unvailability - BETA (Not working)">
            <le-week-availability entity-type="Team" type="UNAVAILABLE" :entity-uuid="$route.params.team"/>
          </ntm-block>
        </div>
      </div>
    </page-content>
    <ntm-sidebar :title="$t('player.create')" name="add-player">
      <form id="playerForm" @submit.prevent="onPlayerSubmit()">
        <formly-form :form="playerForm" :model="playerModel" :fields="playerFields"></formly-form>
        <button class="btn btn-success push-15">{{ $t('save') }}</button>
      </form>
    </ntm-sidebar>
    <modal class="add-players-from-club-teams-modal" name="add-players-from-club-teams" height="auto">
      <div class="text-right p-2">
        <button type="button" class="btn btn-sm btn-light" @click="$modal.hide('add-players-from-club-teams')" v-tooltip="$t('close')">
          <i class="fa fa-times"></i>
        </button>
      </div>
      <div class="p-3 content" v-if="!selectedClubTeam">
        <ntm-table :show-columns="showClubTeamsColumns" :data="clubTeamsForTable"
        >
          <template v-slot:actions="{row}">
            <button @click="selectClubTeam(row)" class="btn btn-sm btn-info" v-tooltip="$t('tooltip.selectTeam')"><i class="fa fa-arrow-right"></i></button>
          </template>
        </ntm-table>
      </div>
      <div class="p-3 content" v-if="selectedClubTeam">
        <div class="d-flex justify-content-between mb-2">
          <button @click="selectedClubTeam = null; selectedClubTeamPlayers = [];" class="btn btn-sm btn-info" v-tooltip="$t('tooltip.backToTeams')"><i
            class="fa fa-arrow-left"></i></button>
          <h3 class="text-center mb-2 mt-2">{{ selectedClubTeam.schedule.league.name }} -
            {{ selectedClubTeam.schedule.season.name }}</h3>
        </div>
        <ntm-table :show-columns="showColumns" :data="selectedClubTeamPlayersForTable"
        >
          <template v-slot:actions="{row}">
            <button @click="selectPlayer(row)" class="btn btn-sm btn-success" v-tooltip="$t('tooltip.addPlayerToTeam')"><i class="fa fa-plus"></i></button>
          </template>
        </ntm-table>
      </div>
    </modal>
    <modal name="edit-player-position" height="auto" :minWidth="300" width="400">
      <div class="p-4" v-if="positionModalPlayer">
        <h5 class="mb-3">{{ positionModalPlayer.name }}</h5>
        <div class="d-flex flex-wrap mb-3" style="gap: 8px;">
          <button
            v-for="pos in playerPositions"
            :key="pos"
            @click="setPosition(pos)"
            class="btn btn-sm"
            :class="positionModalPlayer.position === pos ? 'btn-primary' : 'btn-outline-secondary'"
          >
            {{ pos }}
          </button>
        </div>
        <button @click="setPosition(null)" class="btn btn-sm btn-outline-danger">
          Clear
        </button>
      </div>
    </modal>
    <modal name="edit-player-tag" height="auto" :minWidth="300" width="450">
      <div class="p-4" v-if="tagModalPlayer">
        <h5 class="mb-3">{{ tagModalPlayer.name }}</h5>
        <multiselect
          data-testid="roster-tag-select"
          v-model="tagModalSelectedTags"
          :options="tagOptions"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          :max="3"
          label="name"
          track-by="uuid"
          class="mb-3"
          :placeholder="$tc('playerTag.name', 2)"
        >
          <template slot="option" slot-scope="{ option }">
            <span>{{ option.name }}</span>
            <span v-if="option.is_public" class="badge badge-info ml-1">{{ $t('playerTag.public') }}</span>
          </template>
          <template slot="tag" slot-scope="{ option, remove }">
            <span class="multiselect__tag" :style="tagBadgeStyle(option)">
              <span>{{ option.name }}</span>
              <span v-if="option.is_public" class="badge badge-light ml-1">{{ $t('playerTag.public') }}</span>
              <i class="multiselect__tag-icon" @click.prevent="remove(option)"></i>
            </span>
          </template>
        </multiselect>
        <small v-if="tagModalSelectedTags.length >= 3" class="text-danger d-block mb-3">{{ $t('playerTag.maxTagsReached') }}</small>
        <small v-else-if="hasSelectedPublicTag" class="text-muted d-block mb-3">{{ $t('playerTag.maxPublicTagsReached') }}</small>
        <div class="d-flex" style="gap: 8px;">
          <button data-testid="roster-tag-save" @click="saveTags" class="btn btn-sm btn-success">{{ $t('save') }}</button>
          <button @click="clearTags" class="btn btn-sm btn-outline-danger">{{ $t('clear') }}</button>
        </div>
      </div>
    </modal>
    <modal name="add-from-file" height="auto" :minWidth="400" width="80%">
      <div class="overflow-y-auto px-4 py-2">
        <ntm-block :collapsable="false" :title="$t('team.rosterImportWithAI')">
          <template #options>
            <button type="button" class="btn btn-sm btn-light" @click="$modal.hide('add-from-file')" v-tooltip="$t('close')">
              <i class="fa fa-times"></i>
            </button>
          </template>
          <div  v-if="!addFromFileShowRoster">
            <p>{{$t('team.rosterImportWithAIDescription')}}</p>
            <div class="d-flex flex-wrap w-100 mb-3" style="gap: 10px">
              <button @click="setTab('file')" type="button" class="btn"
                      :class="{'btn-primary': addFromFileTab === 'file'}">
                File
              </button>
              <button @click="setTab('text')" type="button" class="btn"
                      :class="{'btn-primary': addFromFileTab === 'text'}">
                Text
              </button>
            </div>
            <div v-if="addFromFileTab === 'text'">
              <vue-editor  :editorOptions="{}" v-model="addFromFileDataText" ></vue-editor>
              <button class="btn btn-success mt-2 mb-2" :disabled="addFromFileLoading"
                      @click="processText">
                <span>{{ $t('save') }}</span>
                <i v-if="!addFromFileLoading" class="fa fa-save ml-2"></i>
                <i v-if="addFromFileLoading" class="fa fa-cog fa-spin ml-2"></i>
              </button>
            </div>
            <vue-dropzone
              v-if="addFromFileTab === 'file'"
              :useCustomSlot="true"
              ref="myVueDropzone"
              class="mb-3"
              id="dropzone"
              :options="dropzonePlayerOptions"
              @vdropzone-added-files="dropzoneStarted"
              @vdropzone-success="dropzoneResponse"
              @vdropzone-queue-complete="dropzoneQueueCompleted"
            >
              <div class="dropzone-custom-content">
                <h3 class="dropzone-custom-title">{{ $tc('import.aiRoster.window') }}</h3>
                <div class="subtitle">{{ $tc('import.orSelectFromPC') }}</div>
              </div>
            </vue-dropzone>
          </div>

          <div v-if="addFromFileShowRoster">
            <h3 class="text-center mb-2 mt-2">{{ $tc('player.name', 2) }}</h3>
            <div v-if="addFromFileData.players && addFromFileData.players.length" class="table-responsive">
              <table class="table table-bordered table-striped table-vcenter">
                <thead>
                <tr>
                  <th class="">{{ $t('formly.fields.firstName') }}</th>
                  <th class="text-center" style="width: 40px;">
                    <button type="button" class="btn btn-sm btn-warning" @click="swapAllPlayerNames"><i class="fa fa-exchange"></i></button>
                  </th>
                  <th class="">{{ $t('formly.fields.lastName') }}</th>
                  <th class="">{{ $t('formly.fields.birthYear') }}</th>
                  <th class=""></th>
                  <th class="text-center" style="width: 60px;">{{ $tc('action.name', 2) }}</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(player, index) in addFromFileData.players" :key="index">
                  <td>
                    <input @input="playerCheck(index)" class="form-control" v-model="player.firstName">
                  </td>
                  <td class="text-center">
                    <button type="button" class="btn btn-sm btn-warning" @click="swapPlayerName(index)"><i class="fa fa-exchange"></i></button>
                  </td>
                  <td>
                    <input @input="playerCheck(index)" class="form-control" v-model="player.lastName">
                  </td>
                  <td>
                    <input @input="playerCheck(index)" class="form-control" v-model="player.birthYear">
                  </td>
                  <td class="text-center">
                    <span v-if="!player.createNew" class="badge badge-success"><i class="fa fa-check"></i></span>
                    <span v-else class="badge badge-info"><i class="fa fa-user-plus"></i></span>
                  </td>
                  <td class="text-center">
                    <button type="button" class="btn btn-sm btn-danger" @click="deletePlayer(index)"><i class="fa fa-trash"></i></button>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
            <h3 class="text-center mb-2 mt-2">{{ $tc('coach.name', 2) }}</h3>
            <div v-if="addFromFileData.coaches && addFromFileData.coaches.length" class="table-responsive">
              <table class="table table-bordered table-striped table-vcenter">
                <thead>
                <tr>
                  <th class="">{{ $t('formly.fields.firstName') }}</th>
                  <th style="width: 40px;"></th>
                  <th class="">{{ $t('formly.fields.lastName') }}</th>
                  <th class="">{{ $t('formly.fields.birthYear') }}</th>
                  <th class=""></th>
                  <th class="text-center" style="width: 60px;">{{ $tc('action.name', 2) }}</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(coach, index) in addFromFileData.coaches" :key="index">
                  <td>
                    <input class="form-control" v-model="coach.firstName">
                  </td>
                  <td class="text-center">
                    <button type="button" class="btn btn-sm btn-warning" @click="swapCoachName(index)"><i class="fa fa-exchange"></i></button>
                  </td>
                  <td>
                    <input class="form-control" v-model="coach.lastName">
                  </td>
                  <td>
                    <input class="form-control" v-model="coach.birthYear">
                  </td>
                  <td class="text-center">
                    <span v-if="!coach.createNew" class="badge badge-success"><i class="fa fa-check"></i></span>
                    <span v-else class="badge badge-info"><i class="fa fa-user-plus"></i></span>
                  </td>
                  <td class="text-center">
                    <button type="button" class="btn btn-sm btn-danger" @click="deleteCoach(index)"><i class="fa fa-trash"></i></button>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
            <button class="btn btn-success push-15 mr-2" @click="submitRoster" :disabled="addFromFileSubmitting">
              <span>{{ $t('save') }}</span>
              <i v-if="!addFromFileSubmitting" class="fa fa-save ml-2"></i>
              <i v-if="addFromFileSubmitting" class="fa fa-cog fa-spin ml-2"></i>
            </button>
            <button class="btn btn-danger push-15 mr-2" :disabled="addFromFileSubmitting" @click="resetImport">
              <span>{{ $t('cancel') }}</span>
              <i class="fa fa-close ml-2"></i>
            </button>
          </div>
        </ntm-block>
      </div>
    </modal>
  </div>
</template>

<script>
import fields from '../formDefinitions/editTeamFormDefinition'
import TeamService from '../../../services/team.service'
import PlayerTagService from '@/services/playerTag.service'
import seasonShowMixin from '../../../mixins/season.show.mixin'
import _, { debounce } from 'lodash'
import VueI18n from '../../../i18n'
import playerFields from '@/views/players/formDefinition.js'
import { useRouteQuery } from '@/use/useRouteQuery'
import Swal from 'sweetalert2'
import vueDropzone from 'vue2-dropzone'
import { TokenService } from '@/services/storage.service'
import { VueEditor } from 'vue2-editor'
import AIService from '@/services/ai.service'
import PlayerService from '@/services/player.service'
import AddTeamFromApplication from '@/views/schedules/createTeam/AddTeamFromApplication.vue'
import ApplicationWindowService from '@/services/applicationWindow.service'
import Multiselect from 'vue-multiselect'

export default {
  components: { AddTeamFromApplication, vueDropzone, VueEditor, Multiselect },
  mixins: [seasonShowMixin],
  setup () {
    const addPlayer = useRouteQuery('add-player')
    return {
      addPlayer
    }
  },
  data () {
    return {
      addFromFileTab: 'file',
      addFromFileLoading: false,
      addFromFileShowRoster: false,
      addFromFileDataText: '',
      addFromFileData: {
        coaches: [],
        players: []
      },
      addFromFileSubmitting: false,
      playerForm: {},
      playerModel: {},
      playerFields: playerFields,
      form: {},
      model: {
        schedule: {}
      },
      fields: fields,
      playerOptions: [],
      selectedPlayers: [],
      selectedPlayer: null,
      selectedClubTeam: null,
      clubTeams: [],
      selectedClubTeamPlayers: [],
      showClubTeamsColumns: [
        {
          name: 'league',
          prop: 'schedule.league.name'
        },
        {
          name: 'season',
          prop: 'schedule.season.name'
        }
      ],
      playerPositions: [],
      positionModalPlayer: null,
      playerTags: [],
      tagModalPlayer: null,
      tagModalSelectedTags: [],
      showColumns: [
        {
          name: 'name',
          prop: 'name'
        },
        {
          name: 'position',
          prop: 'position',
          slot: true
        },
        {
          name: 'tag',
          prop: 'player_tags',
          slot: true
        }
      ]
    }
  },
  beforeRouteEnter (to, from, next) {
    Promise.all([TeamService.show(to.params.uuid, to.params.team), TeamService.getLatestClubTeams(to.params.uuid, to.params.team)])
      .then((response) => {
        next(vm => vm.setData(response))
      })
  },
  computed: {
    hasSelectedPublicTag () {
      return this.tagModalSelectedTags.some(t => t.is_public)
    },
    tagOptions () {
      const selectedUuids = this.tagModalSelectedTags.map(t => t.uuid)
      return this.playerTags.map(tag => ({
        ...tag,
        $isDisabled: !selectedUuids.includes(tag.uuid) && (
          this.tagModalSelectedTags.length >= 3 ||
          (!!tag.is_public && this.hasSelectedPublicTag)
        )
      }))
    },
    dropzonePlayerOptions () {
      return {
        url: `${process.env.VUE_APP_BASE_URL}api/ai/extract-team-data-from-file`,
        headers: {
          'X-ORGANISATION': this.currentClient.uuid,
          Authorization: `Bearer ${TokenService.getToken()}`
        },
        parallelUploads: 5,
        acceptedFiles: [
          '.csv',
          '.txt',
          '.pdf',
          '.docx',
          '.xls',
          '.xlsx'
        ].join(',')
      }
    },
    currentClient () {
      return this.$store.state.currentClient
    },
    selectedPlayersUuids () {
      return this.selectedPlayers.map(player => player.uuid)
    },
    clubTeamsForTable () {
      return {
        data: this.clubTeams
      }
    },
    selectedPlayersForTable () {
      return {
        data: this.selectedPlayers
      }
    },
    selectedClubTeamPlayersForTable () {
      return {
        data: this.selectedClubTeamPlayers.filter((player) => {
          return !this.selectedPlayersUuids.includes(player.uuid)
        })
      }
    },
    playerFilteredOptions () {
      return this.playerOptions.filter((player) => {
        return !this.selectedPlayersUuids.includes(player.uuid)
      })
    }
  },
  methods: {
    applyTeamApplicationToTeam ({ applicationWindow, teamApplication }) {
      ApplicationWindowService.applyTeamApplicationToTeam(
        applicationWindow.uuid,
        teamApplication.uuid,
        {
          teamUuid: this.$route.params.team
        }
      ).then(() => {
        this.fetchData()
        this.getSelectedPlayers()
      }).catch(() => {
      })
    },
    playerCheck: debounce(function (index) {
      const player = this.addFromFileData.players[index]

      PlayerService.exists(player.firstName, player.lastName, player.birthYear).then((response) => {
        this.addFromFileData.players[index].createNew = !response.data.exists
      }).catch(() => {
      })
    }, 300),
    submitRoster () {
      this.addFromFileSubmitting = true

      TeamService.rosterBulkImport(this.$route.params.uuid, this.$route.params.team, {
        players: this.addFromFileData.players,
        coaches: this.addFromFileData.coaches
      }).then(() => {
        this.$modal.hide('add-from-file')
        this.resetImport()
        this.fetchData()
        this.getSelectedPlayers()
      }).catch((error) => {
        this.addFromFileSubmitting = false
        this.$toast.error(error.response.data.message)
      }).finally(() => {
        this.addFromFileSubmitting = false
      })
    },
    resetImport () {
      this.addFromFileShowRoster = false
      this.addFromFileData = {
        coaches: [],
        players: []
      }
    },
    processText () {
      this.addFromFileLoading = true
      AIService.extractTeamDataFromText(this.addFromFileDataText).then((response) => {
        this.addFromFileData.players = response.data.players
        this.addFromFileData.coaches = response.data.coaches
        this.addFromFileShowRoster = true
      }).catch(() => {
      }).finally(() => {
        this.addFromFileLoading = false
      })
    },
    setTab (tab) {
      this.addFromFileTab = tab
    },
    deletePlayer (index) {
      this.addFromFileData.players.splice(index, 1)
    },
    deleteCoach (index) {
      this.addFromFileData.coaches.splice(index, 1)
    },
    swapPlayerName (index) {
      const player = this.addFromFileData.players[index]
      const tmp = player.firstName
      this.$set(this.addFromFileData.players[index], 'firstName', player.lastName)
      this.$set(this.addFromFileData.players[index], 'lastName', tmp)
      this.playerCheck(index)
    },
    swapAllPlayerNames () {
      this.addFromFileData.players.forEach((player, index) => {
        const tmp = player.firstName
        this.$set(this.addFromFileData.players[index], 'firstName', player.lastName)
        this.$set(this.addFromFileData.players[index], 'lastName', tmp)
        this.playerCheck(index)
      })
    },
    swapCoachName (index) {
      const coach = this.addFromFileData.coaches[index]
      const tmp = coach.firstName
      this.$set(this.addFromFileData.coaches[index], 'firstName', coach.lastName)
      this.$set(this.addFromFileData.coaches[index], 'lastName', tmp)
    },
    dropzoneStarted () {
      this.addFromFileLoading = true
    },
    dropzoneResponse (file, response) {
      this.addFromFileData.players.push(...response.players)
      this.addFromFileData.coaches.push(...response.coaches)
    },
    dropzoneQueueCompleted () {
      this.$refs.myVueDropzone.removeAllFiles()
      this.addFromFileShowRoster = true
      this.addFromFileLoading = false
    },
    selectClubTeam (team) {
      TeamService.getTeamPlayers(team.schedule.uuid, team.uuid).then((res) => {
        this.selectedClubTeam = team

        this.selectedClubTeamPlayers = res.data
      })
    },
    addFromFile () {
      this.$modal.show('add-from-file')
    },
    showAddPlayersFromClubTeamsModal () {
      this.selectedClubTeam = null
      this.selectedClubTeamPlayers = []
      this.$modal.show('add-players-from-club-teams')
    },
    deleteTeam () {
      TeamService.delete(this.$route.params.uuid, this.$route.params.team).then(() => this.$router.push({
        name: 'schedules.show',
        params: { uuid: this.$route.params.uuid }
      }))
    },
    selectPlayer (player) {
      this.selectedPlayer = null
      if (player.uuid === 'add') {
        this.addPlayer = true
        setTimeout(() => {
          const playerFormFirstName = document.querySelector('#playerForm .form-control')
          playerFormFirstName.focus()
        })
        return
      }
      TeamService.addPlayerToTeam(this.$route.params.uuid, this.$route.params.team, { playerUuid: player.uuid }).then(() => {
        this.getSelectedPlayers()
      }).catch((error) => {
        const playerUuidError = error.response.data.errors.playerUuid
        if (playerUuidError) {
          if (playerUuidError[0].includes('player.in_another_team')) {
            this.addPlayerFromAnotherTeam(playerUuidError[0], player)
          } else if (playerUuidError[0].includes('player.in_another_club')) {
            this.addPlayerFromAnotherClub(playerUuidError[0], player)
          }
        }
      }).finally(() => {
        this.focusPlayerSearch()
      })
    },
    addPlayerFromAnotherTeam (error, player) {
      const anotherTeamName = error.split('|')[1]
      return Swal.fire({
        title: VueI18n.t('swal.addPlayerFromAnotherTeam.title', { team: anotherTeamName }),
        text: VueI18n.t('swal.addPlayerFromAnotherTeam.text', { team: anotherTeamName }),
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: VueI18n.t('swal.addPlayerFromAnotherTeam.yes'),
        cancelButtonText: VueI18n.t('swal.addPlayerFromAnotherTeam.no'),
        showCloseButton: true,
        showLoaderOnConfirm: true
      }).then((result) => {
        if (result.value) {
          return TeamService.addPlayerFromAnotherTeam(this.$route.params.uuid, this.$route.params.team, { playerUuid: player.uuid }).then(() => {
            this.getSelectedPlayers()
          })
        }
      })
    },
    addPlayerFromAnotherClub (error, player) {
      const anotherClubName = error.split('|')[1]
      const anotherClubLeagues = error.split('|')[2]
      return Swal.fire({
        title: VueI18n.t('swal.addPlayerFromAnotherClub.title', { club: anotherClubName, leagues: anotherClubLeagues }),
        text: VueI18n.t('swal.addPlayerFromAnotherClub.text', { club: anotherClubName }),
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: VueI18n.t('swal.addPlayerFromAnotherClub.yes'),
        cancelButtonText: VueI18n.t('swal.addPlayerFromAnotherClub.no'),
        showCloseButton: true,
        showLoaderOnConfirm: true
      }).then((result) => {
        if (result.value) {
          return TeamService.addPlayerFromAnotherClub(this.$route.params.uuid, this.$route.params.team, { playerUuid: player.uuid }).then(() => {
            this.getSelectedPlayers()
          })
        }
      })
    },
    focusPlayerSearch () {
      setTimeout(() => {
        const searchInput = document.getElementById('playerSearch')
        searchInput.focus()
      })
    },
    fetchData () {
      TeamService.show(this.$route.params.uuid, this.$route.params.team).then((response) => {
        this.model = response.data
      }).catch((err) => {
        console.error(err)
      })
    },
    setData (response) {
      this.model = response[0].data
      this.clubTeams = response[1].data
      this.fields.find(field => field.key === 'group').options = this.model.groups
      this.getSelectedPlayers()
      this.fetchPlayerPositions()
      this.fetchPlayerTags()
    },
    fetchPlayerTags () {
      PlayerTagService.index().then((res) => {
        this.playerTags = res.data
      })
    },
    tagBadgeStyle (tag) {
      if (!tag || !tag.color) return {}
      return { backgroundColor: tag.color, color: '#fff' }
    },
    openTagModal (player) {
      this.tagModalPlayer = { ...player }
      const uuids = (player.player_tags || []).map(t => t.uuid)
      this.tagModalSelectedTags = this.playerTags.filter(t => uuids.includes(t.uuid))
      this.$modal.show('edit-player-tag')
    },
    saveTags () {
      const player = this.tagModalPlayer
      const uuids = this.tagModalSelectedTags.map(t => t.uuid)
      TeamService.updatePlayerTags(this.$route.params.uuid, this.$route.params.team, player.uuid, {
        player_tag_uuids: uuids
      }).then(() => {
        const found = this.selectedPlayers.find(p => p.uuid === player.uuid)
        if (found) {
          found.player_tags = this.playerTags.filter(t => uuids.includes(t.uuid))
            .map(t => ({ uuid: t.uuid, name: t.name, color: t.color }))
        }
        this.tagModalPlayer = null
        this.tagModalSelectedTags = []
        this.$modal.hide('edit-player-tag')
      })
    },
    clearTags () {
      this.tagModalSelectedTags = []
    },
    searchPlayer (search, loading) {
      if (search.length > 0) {
        loading(true)
        this.searchClubPlayers(search, loading)
      } else {
        this.playerOptions = []
      }
    },
    searchClubPlayers: _.debounce(function (search, loading) {
      TeamService.searchClubPlayers(this.$route.params.uuid, this.$route.params.team, search).then((res) => {
        this.playerOptions = res.data
        if (!this.playerFilteredOptions.length) {
          this.playerOptions.push({ name: this.$t('player.add'), uuid: 'add' })
        }
        loading(false)
      })
    }, 300),
    getSelectedPlayers () {
      TeamService.getTeamPlayers(this.$route.params.uuid, this.$route.params.team).then((res) => {
        this.selectedPlayers = res.data
      })
    },
    fetchPlayerPositions () {
      TeamService.getPlayerPositions().then((res) => {
        this.playerPositions = res.data
      })
    },
    openPositionModal (player) {
      this.positionModalPlayer = { ...player }
      this.$modal.show('edit-player-position')
    },
    setPosition (position) {
      const player = this.positionModalPlayer
      TeamService.updatePlayerPosition(this.$route.params.uuid, this.$route.params.team, player.uuid, { position }).then(() => {
        const found = this.selectedPlayers.find(p => p.uuid === player.uuid)
        if (found) {
          found.position = position
        }
        this.positionModalPlayer = null
        this.$modal.hide('edit-player-position')
      })
    },
    deletePlayerFromTeam (playerUuid) {
      TeamService.deletePlayerFromTeam(this.$route.params.uuid, this.$route.params.team, playerUuid).then(() => {
        this.getSelectedPlayers()
      })
    },
    onSubmit () {
      TeamService.update(this.$route.params.uuid, this.$route.params.team, this.model).then(() => {
        this.$router.push({ name: 'schedules.show', params: { uuid: this.$route.params.uuid } })
      }).catch((err) => {
        this.form.$errors = err.response.data.errors
      })
    },
    onPlayerSubmit () {
      this.playerForm.$errors = {}
      TeamService.createAndAddNewPlayerToTeam(this.$route.params.uuid, this.$route.params.team, this.playerModel).then(() => {
        this.addPlayer = null
        this.playerModel = {}
        this.getSelectedPlayers()
        this.focusPlayerSearch()
      }).catch((err) => {
        this.playerForm.$errors = err.response.data.errors
      })
    }
  }
}
</script>
